import { Injectable } from '@angular/core';
import { TopLevelData } from '@talisdev/jsonapi-transformers';
import { Map } from 'immutable';
import {
  Observable,
  combineLatest,
  throwError as observableThrowError,
} from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { HierarchyNode } from '®shared/hierarchy';
import {
  TarlV3Jsonapi,
  getETagFromMetaData,
  getErrorWithETag,
} from '®shared/tarl-v3-jsonapi';

import { ReadingListDetails } from '../../models';

import {
  HierarchyAndStudentNumbers,
  HierarchyAndStudentNumbersTransfer,
  RawHierarchyAndStudentNumbers,
} from './hierarchy-and-student-numbers';
import { NodeAndOverride } from './node-and-override';

@Injectable()
export class HierarchyAndStudentNumbersService {
  constructor(private tarlV3Jsonapi: TarlV3Jsonapi) {}

  /**
   * Save the hierarchy-attachments and student-number overrides for a list.
   * If `nodesAndOverrides` is specified as part of HierarchyAndStudentNumbers
   * then we only make a single external API call, since updating the nodes
   * will also remove the list-bound student numbers.
   *
   * If `nodesAndOverrides` is not specified then we combine two API calls under
   * the hood:
   * - the list API updates details of the list-bound student numbers
   * - the list-node-relationship API clears node associations
   *
   * Note that although we accept `isDraft`, we always update BOTH published and draft lists;
   * the draft=1 query parameter determines which version is /returned/, not which is saved.
   *
   * @param  {string}                     listId                     A unique identifier for the list
   * @param  {boolean}                    isDraft                    True to return the draft list; false for published list
   * @param  {HierarchyAndStudentNumbers} hierarchyAndStudentNumbers Hierarchy attachments and student numbers for the list
   * @return {Observable<string | null>}  The ETag after updating the list, if available
   */
  saveHierarchyAndStudentNumbersForList(
    listId: string,
    isDraft: boolean,
    hierarchyAndStudentNumbers: HierarchyAndStudentNumbers,
  ): Observable<string | null> {
    const patchHierarchyForList$ =
      this.patchStudentNumberOverridesForListByNode(
        listId,
        hierarchyAndStudentNumbers,
      );

    if (hierarchyAndStudentNumbers.nodesAndOverrides) {
      // save nodesAndOverrides to the list-nodes relationship PATCH API -
      // replacing all node attachments and overridden student numbers
      return patchHierarchyForList$;
    } else {
      // if nodesAndOverrides is empty then we need to
      // first update the list node relationships
      // and then patch the list in sequence to avoid race conditions
      return patchHierarchyForList$.pipe(
        switchMap(etagAfterHierarchyPatch =>
          this.patchStudentNumberOverridesForList(
            listId,
            isDraft,
            hierarchyAndStudentNumbers,
          ).pipe(
            map(
              etagAfterNumbersPatch =>
                etagAfterNumbersPatch || etagAfterHierarchyPatch,
            ),
            catchError(error =>
              observableThrowError(
                getErrorWithETag(error, etagAfterHierarchyPatch),
              ),
            ),
          ),
        ),
      );
    }
  }

  /**
   * Fetch a representation of the hierarchy-and-student-numbers for a list.
   * This combines two API calls under the hood:
   * - the list API provides details of the list-level overrides and node details
   * - the list-node-relationship API provides student-number overrides per node
   *
   * @param  {string} listId A unique identifier for the list
   * @return {Observable<RawHierarchyAndStudentNumbers>} hierarchy attachments and student numbers for the list
   */
  getRawHierarchyAndStudentNumbersForList(
    listId: string,
  ): Observable<RawHierarchyAndStudentNumbers> {
    const overridesByNodeKey$ =
      this.getStudentNumberOverridesForListByNode(listId);
    const list$ = this.getListDetailsWithNodes(listId);

    return combineLatest([list$, overridesByNodeKey$]).pipe(
      map(([list, overridesByNodeKey]) => {
        const listStudentNumbers = list.student_numbers;
        const node2NodeAndOverride = (node: HierarchyNode): NodeAndOverride => {
          const overriddenStudentNumbers = overridesByNodeKey.get(
            nodeKey(node),
            null,
          );
          return { node, overriddenStudentNumbers };
        };
        const nodesAndOverrides = list.nodes.map(node2NodeAndOverride);

        return {
          initialStatus: HierarchyAndStudentNumbersTransfer.NoTransfer,
          listStudentNumbers,
          nodesAndOverrides,
        };
      }),
    );
  }

  /**
   * Fetch the overridden student-numbers for the nodes attached to a list
   * and convert into a map keyed by node key (a pair of <type, id>).
   * This structure is needed as there is no deserialisation here, and
   * the node information is provided by a separate API.
   *
   * @param  {string} listId A unique identifier for the list
   * @return {Observable<Map<string, number | null>>} A map of student-number overrides keyed by node-key
   */
  private getStudentNumberOverridesForListByNode(
    listId: string,
  ): Observable<Map<string, number | null>> {
    const path = this.tarlV3Jsonapi.path(
      '/lists/{listId}/relationships/nodes',
      {
        listId,
      },
    );

    return this.tarlV3Jsonapi
      .getRawJsonapiResponse<TopLevelData>(path)
      .pipe(map(response => extractOverridesByNodeFromResponse(response)));
  }

  /**
   * Fetch list numbers/nodes from the draft version of the list, so that
   * we can support both published and draft data.
   *
   * @param  {string}                         listId The ID of the list
   * @return {Observable<ReadingListDetails>}        List details, including nodes
   */
  private getListDetailsWithNodes(
    listId: string,
  ): Observable<ReadingListDetails> {
    const path = this.tarlV3Jsonapi.path('/lists/{listId}', { listId });
    const params = { include: ['nodes'] };
    return this.tarlV3Jsonapi.getResource<ReadingListDetails>(path, params, {
      draft: '1',
    });
  }

  /**
   * PATCH the overridden student-numbers attached directly to a list.
   *
   * Note that although we accept `isDraft`, we always update BOTH published and draft lists;
   * the draft=1 query parameter determines which version is /returned/, not which is saved.
   *
   * @param {string}                     listId                     A unique identifier for the list
   * @param {boolean}                    isDraft                    True to return the draft list; false for published list
   * @param {HierarchyAndStudentNumbers} hierarchyAndStudentNumbers The hierarchy-and-student-numbers data for the list
   * @return {Observable<string | null>} The ETag after updating the list, if available
   */
  private patchStudentNumberOverridesForList(
    listId: string,
    isDraft: boolean,
    hierarchyAndStudentNumbers: HierarchyAndStudentNumbers,
  ): Observable<string | null> {
    const path = this.tarlV3Jsonapi.path('/lists/{listId}', { listId });
    const list = new ReadingListDetails();
    list.id = listId;
    list.student_numbers = hierarchyAndStudentNumbers.listStudentNumbers;
    const params = isDraft ? { draft: '1' } : {};

    return this.tarlV3Jsonapi
      .patchResource<ReadingListDetails>(path, list, {}, params)
      .pipe(map(updatedList => updatedList.list_etag || null));
  }

  /**
   * Set the hierarchy-nodes for the list, and the overridden student-numbers
   * for each node.
   *
   * @param {string} listId A unique identifier for the list
   * @param {HierarchyAndStudentNumbers} hierarchyAndStudentNumbers The hierarchy-and-student-numbers data for the list
   * @return {Observable<string | null>} The ETag after updating the list, if available
   */
  private patchStudentNumberOverridesForListByNode(
    listId: string,
    hierarchyAndStudentNumbers: HierarchyAndStudentNumbers,
  ): Observable<string | null> {
    const path = this.tarlV3Jsonapi.path(
      '/lists/{listId}/relationships/nodes',
      {
        listId,
      },
    );

    const data = (hierarchyAndStudentNumbers.nodesAndOverrides || []).map(
      getRelationshipFromNodeAndOverride,
    );

    return this.tarlV3Jsonapi
      .patchToManyRelationship<NodeRelationshipJsonapi>(path, data)
      .pipe(map(getETagFromMetaData));
  }
}

export type HierarchyRelationship = {
  id: string;
  type: string;
  override_student_numbers: number | null;
};

/**
 * Deserialise a list-nodes-relationship response to extract the student-numbers per node,
 * keyed by node-key (a pair of <type, id>)
 * @param  {JsonapiResponse} response A JSON:API response from the list-nodes-relationships API
 * @return {Map<string, number | null>} A map of student-number overrides keyed by node-key
 */
export function extractOverridesByNodeFromResponse(
  response: TopLevelData,
): Map<string, number | null> {
  const { data = [] } = response || {};
  const overridesByNodeKeyReducer = (
    overridesByKey: Map<string, number | null>,
    relationship: HierarchyRelationship,
  ) =>
    overridesByKey.set(
      typeAndIdToKey(relationship.type, relationship.id),
      relationship.override_student_numbers,
    );

  return data
    .map(getHierarchyRelationship)
    .filter(relationship => (relationship ? true : false))
    .reduce(overridesByNodeKeyReducer, Map<string, number | null>());
}

/**
 * Deserialise a single list-node relationship, if possible
 * @param  {any} json A JSON object, possibly describing a list-node relationship
 * @return {HierarchyRelationship|null} Student-numbers override for list-node relationship, if available
 */
export function getHierarchyRelationship(
  json: any,
): HierarchyRelationship | null {
  const { id = null, type = null, meta = {} } = json || {};

  const override_student_numbers =
    meta && Number.isInteger(meta.override_student_numbers)
      ? meta.override_student_numbers
      : null;

  if (!id || !type) {
    return null;
  }

  return { id, type, override_student_numbers };
}

/**
 * Convert a HierarchyNode to a node-key (a <type, id> pair)
 * @param  {HierarchyNode}} A node
 * @return {string}         A node-key (a <type, id> pair)
 */
export function nodeKey(node: HierarchyNode): string {
  return typeAndIdToKey(node.normalisedType, node.id);
}

export function typeAndIdToKey(type: string, id: string): string {
  return `${type}___${id}`;
}

export type NodeRelationshipJsonapi = {
  id: string;
  type: string;
  meta: {
    override_student_numbers: number | null;
  };
};

export function getRelationshipFromNodeAndOverride(
  nodeAndOverride: NodeAndOverride,
): NodeRelationshipJsonapi {
  const { id, normalisedType } = nodeAndOverride.node;
  const meta = {
    override_student_numbers: nodeAndOverride.overriddenStudentNumbers || null,
  };
  return { id, meta, type: normalisedType };
}
